import { Combobox } from "@msuite/ui/form";
import { useDisclosure } from "@msuite/ui/hooks";
import { useTranslation } from "@msuite/ui/localization";
import { Fragment } from "@msuite/ui/utils";
import { type Control, type FieldValues, type Path, useController } from "react-hook-form";
import { useContacts } from "@/hooks/use-contacts";
import { AddContactModal } from "@/modals/contacts/add-contact-modal";

/** Props Interface */
interface ContactPickerProps<T extends FieldValues> {
	control: Control<T>;
	name: Path<T>;
	label?: string;
	isNullable?: boolean;
}

export function ContactPicker<T extends FieldValues>({
	control,
	name,
	label,
	isNullable,
}: ContactPickerProps<T>) {
	/** Hooks */
	const { t } = useTranslation();
	const disclosure = useDisclosure();
	const controller = useController({ control, name });

	/** Query */
	const { contacts } = useContacts();

	return (
		<Fragment>
			<Combobox
				control={control}
				name={name}
				options={contacts}
				displayValue={(item) => item?.name || "n/a"}
				label={label || t("projects.fields.client")}
				keyExtractor={(item) => item?.id}
				placeholder={t("placeholders.company_name")}
				isRequired={false}
				isNullable={isNullable}
				data-cy="project-client-input"
				buttons={[
					{
						tags: ["add contact"],
						render: () => (
							<Combobox.Button iconName="Plus">{t("contacts.add_contact")}</Combobox.Button>
						),
						onClick: disclosure.onOpen,
					},
				]}
			/>
			{disclosure.isOpen && (
				<AddContactModal
					isOpen={disclosure.isOpen}
					onClose={disclosure.onClose}
					onComplete={(item) => {
						controller.field.onChange(item.id);
					}}
				/>
			)}
		</Fragment>
	);
}
